import * as ngCore from '@angular/core'
import * as ngRouter from '@angular/router'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as router from '@attain-apps/fe/web/main/lib/router'
import { capitalize } from '@attain-apps/string-utils'

import * as state from '../../../../../../../state'
import * as modals from '../../modals'

import * as general from './general'
import * as organizations from './organization-assignments'

@ngCore.Component({
  imports: [
    ds.input.text.Component,
    ds.layout.widget.Component,
    ds.layout.page.Component,
    ds.layout.page.child.header.Component,
    ds.overlay.centered.Component,
    ds.overlay.attached.Component,
    ds.overlay.confirmation.Component,
    general.Component,
    organizations.Component,
    ds.tag.Component,
    modals.recordHistoryLog.Component,
  ],
  selector: 'atn-space-lite-record-details',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link router.Router} */
  private readonly _router = ngCore.inject(router.Router)
  /** Activated route used to pass relative path in breadcrumb */
  private readonly _activatedRoute = ngCore.inject(ngRouter.ActivatedRoute)

  /** @see {@link state.space.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.space.lite.records.Collection)

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link state.space.surveyConfiguration.Feature} */
  private readonly _surveyConfigurationFeature = ngCore.inject(
    state.space.surveyConfiguration.Feature,
  )

  /** @see {@link state.space.userAccess.surveyAdmins.Collection} */
  private readonly _surveyAdminsEc = ngCore.inject(
    state.space.userAccess.surveyAdmins.Collection,
  )

  /** @see {@link state.space.lite.records.plugins.surveyStatus.Plugin} */
  public readonly surveyStatusPlugin = this.recordsEc.plugins
    .lookup(state.space.lite.records.plugins.surveyStatus.Plugin)
    .get()

  /** Exposes {@link Operation} to the template */
  public readonly OperationEnum =
    state.space.lite.records.plugins.surveyStatus.Operation

  /** @see {@link state.space.lite.records.plugins.functionalUsage.Plugin} */
  public readonly functionalUsagePlugin = this.recordsEc.plugins
    .lookup(state.space.lite.records.plugins.functionalUsage.Plugin)
    .get()

  /**
   * Style enum exposed to HTML
   *
   * @see {@link ds.color.Legacy.PredefinedStyle}
   */
  public readonly TagStyleEnum = ds.color.Legacy.PredefinedStyle

  /**
   * Whether the record is vacant.
   */
  public readonly isVacantSg = ngCore.computed(
    (
      methodology = this.functionalUsagePlugin.selectors.methodology.asSignal(),
    ) =>
      methodology ===
      ents.extra.space.Record.FunctionalUsage.Methodology.Vacant,
  )

  /**
   * Controls whether record history log overlay is currently visible
   */
  public readonly presentRecordHistoryLogSg = ngCore.signal(false)

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** Selected record */
  private readonly _recordSg = ngCore.computed(() =>
    this.recordsEc.plugins.currentEntityPlugin.feature.selectors
      .getEntity()
      .asSignal(),
  )

  /** Selected record name */
  public readonly recordNameSg = ngCore.computed(
    (record = this._recordSg()) => record?.name,
  )

  /** Selected survey status */
  // NOTE: In Space Lite - it is lite status, while in Space Full status can be different
  public readonly surveyStatusSg = ngCore.computed(
    (record = this._recordSg()) => record?.surveyStatusLite,
  )

  /** Whether the current record's lite survey has already been signed off */
  public readonly isSignedOffSg = ngCore.computed(
    (surveyStatus = this.surveyStatusSg()) =>
      surveyStatus === ents.extra.space.SurveyStatus.SignedOff,
  )

  /**
   * Whether the current user is allowed to unlock a signed-off room: either
   * Survey Configuration allows any user to unlock directly, or the current
   * user is a Platform Admin, Tenant Admin, or Survey Admin.
   */
  public readonly canUnlockSg = ngCore.computed(
    (
      config = this._surveyConfigurationFeature.selectors.config.asSignal(),
      canManageUsers = this._meFeature.contexts.selectors.currentUserCanManageUsers.asSignal(),
      currentUserId = this._meFeature.selectors.currentUserId.asSignal(),
      surveyAdminUserIds = new Set(
        this._surveyAdminsEc.plugins.currentListPlugin.feature.selectors.entities
          .asSignal()
          .map(({ userId }) => userId),
      ),
    ) =>
      config?.tools.allowUsersToUnlockRoomsDirectly === true ||
      !!(
        currentUserId &&
        (canManageUsers || surveyAdminUserIds.has(currentUserId))
      ),
  )

  /** Selected record ancestors */
  public readonly recordAncestorsSg = ngCore.computed(
    (
      record = this._recordSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => {
      if (!record) {
        return []
      }
      const ancestors = schema.getAncestorsOf(record?.entityId)
      return ancestors
        ? ancestors.map(ancestor => ({
            name: ancestor.name,
            value:
              ancestor.id === record.entityId
                ? record.name
                : record.ancestors[ancestor.id]?.name,
          }))
        : []
    },
  )

  /** Breadcrumbs, only the first element in array should be clickable */
  public readonly breadcrumbsSg = ngCore.computed(
    (ancestors = this.recordAncestorsSg()) => {
      const breadcrumbs: ds.layout.page.child.header.models.Breadcrumb[] =
        ancestors.map(ancestor => ({
          name: ancestor.value,
        }))

      // We want only the institution to be clickable so we pass it the url
      // to records list page
      breadcrumbs.unshift({
        name: 'Institution',
        url: this._router.ngRouter.createUrlTree(
          [`../${ents.extra.prebuilt.Page.Id.SpaceLiteRecordsList}`],
          {
            queryParamsHandling: 'preserve',
            relativeTo: this._activatedRoute,
          },
        ),
      } satisfies ds.layout.page.child.header.models.Breadcrumb)
      // Push record name as last breadcrumb
      breadcrumbs.push({
        name: this.recordNameSg() ?? 'Record',
      } satisfies ds.layout.page.child.header.models.Breadcrumb)

      return breadcrumbs
    },
  )

  /** Opens the confirmation dialog for the given survey lock operation. */
  public initiateClickHandler(
    operation: state.space.lite.records.plugins.surveyStatus.Operation,
  ): void {
    this.surveyStatusPlugin.actions.initiate
      .from(`LiteRecordDetails.${capitalize(operation)}.Icon.Clicked`)
      .dispatch({ operation })
  }

  /** Confirms and submits the given survey lock operation. */
  public confirmClickHandler(
    operation: state.space.lite.records.plugins.surveyStatus.Operation,
  ): void {
    this.surveyStatusPlugin.actions.submit
      .from(
        `LiteRecordDetails.${capitalize(operation)}.Confirmation.Footer.Buttons.Confirm.Clicked`,
      )
      .dispatch({ operation })
  }

  /** Cancels the confirmation dialog for the given survey lock operation. */
  public cancelClickHandler(
    operation: state.space.lite.records.plugins.surveyStatus.Operation,
  ): void {
    this.surveyStatusPlugin.actions.cancel
      .from(
        `LiteRecordDetails.${capitalize(operation)}.Confirmation.Footer.Buttons.Cancel.Clicked`,
      )
      .dispatch({ operation })
  }
}
